/**
 * Single source of truth for status/priority coloring (Atlas HUD spec §2).
 * Values are CSS custom properties defined in index.css, so they follow the
 * active theme. Replaces the per-page STATUS_COLORS/PRIORITY_COLORS consts.
 */
export type TaskStatus = 'todo' | 'in_progress' | 'review' | 'blocked' | 'done';
export type TaskPriority = 'low' | 'medium' | 'high' | 'urgent';

const STATUS_VARS: Record<TaskStatus, string> = {
    todo: 'var(--status-todo)',
    in_progress: 'var(--status-in-progress)',
    review: 'var(--status-review)',
    blocked: 'var(--status-blocked)',
    done: 'var(--status-done)',
};

const PRIORITY_VARS: Record<TaskPriority, string> = {
    low: 'var(--priority-low)',
    medium: 'var(--priority-medium)',
    high: 'var(--priority-high)',
    urgent: 'var(--priority-urgent)',
};

export function statusColor(status: string): string {
    return STATUS_VARS[status as TaskStatus] ?? 'var(--ink-muted)';
}

export function priorityColor(priority: string): string {
    // 'critical' is legacy frontend drift (WorkloadPage/TimelinePage); the
    // backend enum is low|medium|high|urgent.
    const key = priority === 'critical' ? 'urgent' : priority;
    return PRIORITY_VARS[key as TaskPriority] ?? 'var(--ink-muted)';
}

type AlphaToken = 'accent' | 'warn' | 'ok' | 'danger' | 'ink' | 'surface';

/** Alpha over a theme token, e.g. varAlpha('accent', .2) for a teal tint. */
export function varAlpha(token: AlphaToken, alpha: number): string {
    return `rgba(var(--${token}-rgb), ${alpha})`;
}

const PRIORITY_TINT_TOKEN: Record<TaskPriority, AlphaToken> = {
    low: 'ink',
    medium: 'accent',
    high: 'warn',
    urgent: 'danger',
};

/** Translucent tint matching priorityColor(), for badge/chip backgrounds. */
export function priorityTint(priority: string, alpha: number): string {
    const key = priority === 'critical' ? 'urgent' : priority;
    return varAlpha(PRIORITY_TINT_TOKEN[key as TaskPriority] ?? 'ink', alpha);
}

/**
 * Alpha over an arbitrary hex color (user-persisted project/phase colors).
 * Replaces the `color + '30'` hex-string concatenation pattern.
 */
export function hexAlpha(hex: string, alpha: number): string {
    const h = hex.replace('#', '');
    const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
    const n = parseInt(full.slice(0, 6), 16);
    // Anything that isn't a full 3- or 6-digit hex (including 4-5 char partials
    // like '2DE8') deliberately falls back to black rather than a partial parse.
    if (full.length < 6 || Number.isNaN(n)) return `rgba(0, 0, 0, ${alpha})`;
    return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}

/** Readable text color (near-black or white) for an arbitrary hex fill. */
export function onHex(hex: string): string {
    const h = hex.replace('#', '');
    const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
    const n = parseInt(full.slice(0, 6), 16);
    if (full.length < 6 || Number.isNaN(n)) return '#FFFFFF';
    const lin = (c: number) => {
        const s = c / 255;
        return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
    };
    const L = 0.2126 * lin((n >> 16) & 255) + 0.7152 * lin((n >> 8) & 255) + 0.0722 * lin(n & 255);
    // 0.179 is the luminance where black and white text have equal contrast.
    return L > 0.179 ? '#04100E' : '#FFFFFF';
}
